import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, getDoc, doc } from "firebase/firestore";
import { Group, Avatar, Text, Badge, Card, Stack, Box, Divider, useMantineTheme } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconMail, IconPhone, IconPackage, IconCalendar, IconFingerprint } from "@tabler/icons-react";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";

export default function BiometricCheckInListener() {
  const companyId = useAppSelector((state: RootState) => state.session?.company_id);
  const theme = useMantineTheme();
  const [sessionStartTime] = useState(new Date());

  useEffect(() => {
    if (!companyId) return;

    let isInitialLoad = true;

    const attendanceHistoryRef = collection(db, "attendancehistory");
    const q = query(
      attendanceHistoryRef,
      where("company_id", "==", companyId),
      where("time_out", "==", "")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Ignore the very first snapshot which contains all people currently checked in
      if (isInitialLoad) {
        isInitialLoad = false;
        return;
      }

      snapshot.docChanges().forEach(async (change) => {
        const logData = change.doc.data();
        const memberId = logData.member_id || logData.admin_id;
        const branchId = logData.branch_id;
        const userType = logData.member_type; // "employee" or "member"

        if (!memberId) return;

        if (change.type === "added") {
          // Tell the rest of the app that this person checked in
          window.dispatchEvent(
            new CustomEvent("biometricStatusChange", {
              detail: { memberId, status: true },
            })
          );

          // Fetch user details to get photo and package info
          const userDocPath =
            userType === "employee"
              ? `admin/${memberId}`
              : `branch/${branchId}/members/${memberId}`;
              
          try {
            const userDocSnap = await getDoc(doc(db, userDocPath));
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              showCheckInModal(userData, logData);
            } else {
              // Fallback to basic log data if user doc missing
              showCheckInModal(logData, logData);
            }
          } catch (error) {
            console.error("Error fetching user for checkin popup:", error);
          }
        } else if (change.type === "removed") {
          // Tell the rest of the app that this person checked out
          window.dispatchEvent(
            new CustomEvent("biometricStatusChange", {
              detail: { memberId, status: false },
            })
          );
        }
      });
    });

    return () => unsubscribe();
  }, [companyId]);

  const showCheckInModal = (userData: any, logData: any) => {
    // Generate a unique modal ID so we can close it safely later
    const modalId = `checkin-${logData.uid || Date.now()}-${Math.random()}`;
    const name = userData?.name || `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim() || logData?.name;
    const photo = userData?.avatar || userData?.photo_url || null;
    const timeIn = logData.time_in?.toDate ? logData.time_in.toDate().toLocaleTimeString() : new Date().toLocaleTimeString();
    
    // Safely parse subscription end date if it exists
    let expiryDate = "N/A";
    if (userData?.subscription_end_date) {
      try {
        expiryDate = userData.subscription_end_date?.toDate 
          ? userData.subscription_end_date.toDate().toLocaleDateString() 
          : new Date(userData.subscription_end_date).toLocaleDateString();
      } catch (e) {
        expiryDate = "Invalid Date";
      }
    }

    const primaryColor = theme.colors[theme.primaryColor]?.[6] || "#f2354b";
    const primaryLight = theme.colors[theme.primaryColor]?.[0] || "#ffe5e7";

    notifications.show({
      id: modalId,
      autoClose: 4000,
      withCloseButton: true,
      color: theme.primaryColor,
      title: "Check-In Successful",
      icon: <IconCheck size={18} />,
      message: (
        <Group align="flex-start" noWrap mt={4} spacing="md">
          <Avatar
            src={photo}
            size={56}
            radius={56}
            style={{ 
              border: `2px solid ${primaryLight}`
            }}
          >
            {name ? name.substring(0, 2).toUpperCase() : "??"}
          </Avatar>
          
          <Stack spacing={6} style={{ flex: 1, overflow: "hidden" }}>
            <Group position="apart" noWrap>
              <Text weight={700} size="md" style={{ lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</Text>
              {userData?.status && (
                <Badge color={userData.status.toLowerCase() === "active" ? "green" : "red"} size="xs" variant="filled">
                  {userData.status}
                </Badge>
              )}
            </Group>

            <Stack spacing={4}>
              <Group spacing={8} noWrap>
                <IconPhone size={14} color="gray" style={{ minWidth: 14 }} />
                <Text size="xs" color="dimmed" style={{ wordBreak: "break-word" }}>
                  {userData?.mobile_number || "No phone"}
                </Text>
              </Group>

              {userData?.email && (
                <Group spacing={8} noWrap>
                  <IconMail size={14} color="gray" style={{ minWidth: 14 }} />
                  <Text size="xs" color="dimmed" style={{ wordBreak: "break-all" }}>
                    {userData.email}
                  </Text>
                </Group>
              )}

              <Group spacing={8} noWrap>
                <IconPackage size={14} color="gray" style={{ minWidth: 14 }} />
                <Text size="xs" color="dimmed" style={{ wordBreak: "break-word" }}>
                  {userData?.current_pkg_name || "No Active Package"}
                </Text>
              </Group>

              {expiryDate !== "N/A" && (
                <Group spacing={8} noWrap>
                  <IconCalendar size={14} color="gray" style={{ minWidth: 14 }} />
                  <Text size="xs" color="dimmed" style={{ wordBreak: "break-word" }}>
                    Expires: {expiryDate}
                  </Text>
                </Group>
              )}
            </Stack>

            <Group spacing={6} mt={2}>
              <IconFingerprint size={16} color={primaryColor} />
              <Text weight={600} color={primaryColor} size="sm">
                Time: {timeIn}
              </Text>
            </Group>
          </Stack>
        </Group>
      ),
    });
  };

  return null; // This component only listens, no static UI
}
